// src/contexts/ChannelsDataProvider.tsx
//
// Single source of truth for channels + device-groups data, scoped to the
// currently selected org. Sits above any provider that needs the channel
// list (VoiceProvider, ChannelsProvider). Page-specific UI state (table
// filters, sort, pagination) lives in ChannelsProvider, not here.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import { useGlobal } from './GlobalProvider'
import { Channel } from 'lib/types/channels'
import type { DeviceGroup } from 'lib/types/deviceGroups'
import { getChannels as apiGetChannels } from 'models/api/channels'
import { getDeviceGroups, upsertDeviceGroup } from 'models/api/deviceGroups'

type ChannelsDataContextType = {
  channels: Channel[]
  deviceGroups: DeviceGroup[]

  loading: boolean
  loadingChannels: boolean
  loadingDeviceGroups: boolean
  // True after the first channels fetch for the current org has completed
  // (success OR failure). Consumers gate on this before reading `channels`
  // for things like hydrating saved preferences — otherwise they'd see an
  // empty list and write empty prefs back to localStorage.
  channelsLoaded: boolean
  // The orgId of the channels currently in `channels`. During an org
  // switch there's a render where `selectedOrgId` is the new org but the
  // channels in state are still the previous org's (sibling effects fire
  // before the parent's data fetch begins). Consumers that derive state
  // from channels (e.g. VoiceProvider's prefs hydration) should verify
  // this matches `selectedOrgId` before treating the list as authoritative.
  channelsOrgId: string | null

  setChannels: React.Dispatch<React.SetStateAction<Channel[]>>
  setDeviceGroups: React.Dispatch<React.SetStateAction<DeviceGroup[]>>

  refreshChannels: () => Promise<void>
  refreshDeviceGroups: () => Promise<void>
  refreshAll: () => Promise<void>

  upsertDeviceGroupOptimistic: (payload: any) => Promise<DeviceGroup>
}

const ChannelsDataContext = createContext<ChannelsDataContextType | null>(null)

export const useChannelsData = (): ChannelsDataContextType => {
  const ctx = useContext(ChannelsDataContext)
  if (!ctx) throw new Error('useChannelsData must be used within a ChannelsDataProvider')
  return ctx
}

type Props = { children: ReactNode }

export const ChannelsDataProvider: React.FC<Props> = ({ children }) => {
  const [channels, setChannels] = useState<Channel[]>([])
  const [deviceGroups, setDeviceGroups] = useState<DeviceGroup[]>([])

  const [loading, setLoading] = useState(false)
  const [loadingChannels, setLoadingChannels] = useState(false)
  const [loadingDeviceGroups, setLoadingDeviceGroups] = useState(false)
  const [channelsLoaded, setChannelsLoaded] = useState(false)
  const [channelsOrgId, setChannelsOrgId] = useState<string | null>(null)

  const { selectedOrgId, refreshFlag, setRefreshFlag } = useGlobal()
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  const clearForNoOrg = useCallback(() => {
    setChannels([])
    setDeviceGroups([])
    setChannelsLoaded(false)
    setChannelsOrgId(null)
  }, [])

  const loadChannels = useCallback(async () => {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      clearForNoOrg()
      return
    }

    // Capture the org we're fetching for so we can stamp the result.
    const fetchOrgId = selectedOrgId

    setLoadingChannels(true)
    setChannelsLoaded(false)
    // Invalidate the orgId stamp synchronously — until the new fetch
    // completes successfully, no consumer should treat `channels` as
    // authoritative for the new org.
    setChannelsOrgId(null)
    try {
      const list = await apiGetChannels(fetchOrgId)
      if (!aliveRef.current) return
      // If the user switched orgs mid-fetch, drop this response.
      if (fetchOrgId !== selectedOrgId) return
      setChannels(Array.isArray(list) ? list : [])
      setChannelsOrgId(fetchOrgId)
      // Only mark loaded on SUCCESS. If the fetch fails (transient 401,
      // network blip, etc.) we leave `channelsLoaded=false` so consumers
      // (e.g. VoiceProvider's prefs hydration) don't run with an empty
      // channels list and overwrite saved prefs in localStorage.
      setChannelsLoaded(true)
    } catch (err) {
      if (!aliveRef.current) return
      console.error('Failed to load channels:', err)
      setChannels([])
    } finally {
      if (aliveRef.current) setLoadingChannels(false)
    }
  }, [selectedOrgId, clearForNoOrg])

  const loadDeviceGroups = useCallback(async () => {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      clearForNoOrg()
      return
    }

    setLoadingDeviceGroups(true)
    try {
      const list = await getDeviceGroups(selectedOrgId)
      if (!aliveRef.current) return
      setDeviceGroups(Array.isArray(list) ? list : [])
    } catch (err) {
      if (!aliveRef.current) return
      console.error('Failed to load device groups:', err)
      setDeviceGroups([])
    } finally {
      if (aliveRef.current) setLoadingDeviceGroups(false)
    }
  }, [selectedOrgId, clearForNoOrg])

  const loadAll = useCallback(async () => {
    if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
      clearForNoOrg()
      return
    }
    setLoading(true)
    try {
      await Promise.all([loadChannels(), loadDeviceGroups()])
    } finally {
      if (aliveRef.current) setLoading(false)
    }
  }, [selectedOrgId, clearForNoOrg, loadChannels, loadDeviceGroups])

  const refreshChannels = useCallback(async () => {
    await loadChannels()
  }, [loadChannels])

  const refreshDeviceGroups = useCallback(async () => {
    await loadDeviceGroups()
  }, [loadDeviceGroups])

  const refreshAll = useCallback(async () => {
    await loadAll()
  }, [loadAll])

  const upsertDeviceGroupOptimistic = useCallback(
    async (payload: any): Promise<DeviceGroup> => {
      if (!selectedOrgId || selectedOrgId === ALL_ORGS_SELECTED) {
        throw new Error('No org selected')
      }

      const hasId = Boolean(payload?.id)
      const tempId = hasId ? null : `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`

      if (hasId) {
        const id = String(payload.id)
        setDeviceGroups(prev =>
          prev.map(dg => (String(dg.id) === id ? ({ ...dg, ...payload, orgId: selectedOrgId } as any) : dg)),
        )
      } else {
        setDeviceGroups(prev => [
          {
            id: tempId,
            orgId: selectedOrgId,
            name: payload?.name || '',
            language: payload?.language,
            channelName: payload?.channelName || '',
            color: payload?.color,
            insertedAt: new Date(),
            updatedAt: new Date(),
            alertSources: [],
          } as any as DeviceGroup,
          ...prev,
        ])
      }

      const saved = await upsertDeviceGroup({ ...payload, orgId: selectedOrgId })

      setDeviceGroups(prev => {
        const sid = String((saved as any)?.id ?? '')
        const next = prev
          .filter(dg => String(dg.id) !== String(tempId || ''))
          .map(dg => (String(dg.id) === sid ? saved : dg))

        const exists = next.some(dg => String(dg.id) === sid)
        return exists ? next : [saved, ...next]
      })

      return saved
    },
    [selectedOrgId],
  )

  // Load when org changes.
  useEffect(() => {
    void loadAll()
  }, [loadAll])

  // Honor the global refreshFlag.
  useEffect(() => {
    if (!refreshFlag) return
    ;(async () => {
      await loadAll()
      setRefreshFlag(false)
    })()
  }, [refreshFlag, loadAll, setRefreshFlag])

  return (
    <ChannelsDataContext.Provider
      value={{
        channels,
        deviceGroups,
        loading,
        loadingChannels,
        loadingDeviceGroups,
        channelsLoaded,
        channelsOrgId,
        setChannels,
        setDeviceGroups,
        refreshChannels,
        refreshDeviceGroups,
        refreshAll,
        upsertDeviceGroupOptimistic,
      }}
    >
      {children}
    </ChannelsDataContext.Provider>
  )
}
